/**
 * Product Badge Updater
 * Aggiorna i badge in base alla variante corrente.
 *
 * Requisito: usare il valore `?variant=` in URL (impostato dal tema via `history.replaceState`).
 */

const LOCATION_CHANGE_EVENT = 'product-badge-updater:locationchange';

function installLocationChangeHook() {
  if (window.__productBadgeUpdaterLocationHookInstalled) return;
  window.__productBadgeUpdaterLocationHookInstalled = true;

  const dispatch = () => window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));

  const { pushState, replaceState } = window.history;
  window.history.pushState = function (...args) {
    const result = pushState.apply(this, args);
    dispatch();
    return result;
  };
  window.history.replaceState = function (...args) {
    const result = replaceState.apply(this, args);
    dispatch();
    return result;
  };

  window.addEventListener('popstate', dispatch);
  window.addEventListener('hashchange', dispatch);
}

function getVariantIdFromUrl() {
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('variant');
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function coerceVariantId(raw) {
  if (!raw) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;

  // Support Shopify GraphQL GID values: gid://shopify/ProductVariant/123
  if (raw.startsWith('gid://')) {
    const last = raw.split('/').pop();
    if (!last) return null;
    const parsed = Number(last);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeBadgesData(parsedData) {
  if (!parsedData) return [];

  /** @type {any[]} */
  let badges = [];
  if (Array.isArray(parsedData)) badges = parsedData;
  else if (Array.isArray(parsedData.badges)) badges = parsedData.badges;

  // Backward compatibility: previous Liquid generated `badges: [[{...}], [{...}]]`
  if (badges.length && Array.isArray(badges[0])) {
    badges = badges.map((entry) => (Array.isArray(entry) ? entry[0] : entry)).filter(Boolean);
  }

  return badges
    .filter((badge) => badge && typeof badge === 'object')
    .map((badge) => {
      const variants = Array.isArray(badge.variants) ? badge.variants.map(coerceVariantId).filter(Boolean) : [];
      return {
        ...badge,
        variants,
        discountVisible: Boolean(badge.discountVisible),
        discountVisibleMobile: badge.discountVisibleMobile !== undefined ? Boolean(badge.discountVisibleMobile) : true,
      };
    })
    .filter((badge) => badge.variants.length > 0);
}

function computeDiscountPercentage(variantResource) {
  if (!variantResource || typeof variantResource !== 'object') return 0;
  const price = Number(variantResource.price);
  const compareAtPrice = Number(variantResource.compare_at_price);
  if (!Number.isFinite(price) || !Number.isFinite(compareAtPrice)) return 0;
  if (compareAtPrice <= 0 || compareAtPrice <= price) return 0;
  return Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
}

class ProductBadgeUpdater {
  /**
   * @param {HTMLElement} badgeContainer
   */
  constructor(badgeContainer) {
    this.badgeContainer = badgeContainer;
    this.closestSection = this.badgeContainer?.closest('.shopify-section, dialog') || null;

    installLocationChangeHook();

    this.badges = this.parseBadgesData();
    this.variantToBadge = this.buildVariantMap(this.badges);

    // Shared cache across instances (useful when the badge block lives in another section)
    if (!window.__productBadgeUpdaterVariantResourceById) {
      window.__productBadgeUpdaterVariantResourceById = new Map();
    }
    this.lastVariantResourceById = window.__productBadgeUpdaterVariantResourceById;

    this.currentVariantId = null;

    this.onLocationChange = this.onLocationChange.bind(this);
    this.onVariantUpdate = this.onVariantUpdate.bind(this);

    this.init();
  }

  parseBadgesData() {
    if (!this.badgeContainer) return [];

    const root = this.closestSection || document;
    const scopedEl = root.querySelector('[data-badges-json]') || document.querySelector('[data-badges-json]');

    if (!scopedEl) return [];

    try {
      const parsed = JSON.parse(scopedEl.textContent || 'null');
      return normalizeBadgesData(parsed);
    } catch (error) {
      console.error('ProductBadgeUpdater: errore parsing badges JSON', error);
      return [];
    }
  }

  buildVariantMap(badges) {
    const map = new Map();
    for (const badge of badges) {
      for (const variantId of badge.variants || []) {
        map.set(Number(variantId), badge);
      }
    }
    return map;
  }

  init() {
    window.addEventListener(LOCATION_CHANGE_EVENT, this.onLocationChange);
    document.addEventListener('variant:update', this.onVariantUpdate);

    // Initial render
    this.updateFromUrl();
  }

  destroy() {
    window.removeEventListener(LOCATION_CHANGE_EVENT, this.onLocationChange);
    document.removeEventListener('variant:update', this.onVariantUpdate);
  }

  onLocationChange() {
    this.updateFromUrl();
  }

  /**
   * @param {any} event
   */
  onVariantUpdate(event) {
    const resource = event?.detail?.resource;
    const id = coerceVariantId(resource?.id);
    if (id) this.lastVariantResourceById.set(id, resource);
    // Re-render using URL as source of truth (after the theme updated it)
    queueMicrotask(() => this.updateFromUrl());
  }

  getVariantIdFallbackFromDom() {
    // If there's no `?variant=` (rare on product pages), fallback to the product form input.
    const input =
      document.querySelector('form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('[data-product-form] [name="id"]') ||
      document.querySelector('[name="id"]');
    return coerceVariantId(input instanceof HTMLInputElement ? input.value : null);
  }

  updateFromUrl() {
    const variantId = getVariantIdFromUrl() ?? this.getVariantIdFallbackFromDom();
    if (!variantId) return;
    this.currentVariantId = variantId;
    const variantResource = this.lastVariantResourceById.get(variantId) || null;
    this.updateBadgeForVariant(variantId, variantResource);
  }

  ensureBadgeContainer() {
    return this.badgeContainer || null;
  }

  updateBadgeForVariant(variantId, variantResource = null) {
    if (!variantId) return;
    this.currentVariantId = variantId;
    const badge = this.variantToBadge.get(Number(variantId)) || null;
    if (!badge) {
      this.clearBadge();
      return;
    }
    this.renderBadge(badge, variantResource);
  }

  renderBadge(badge, variantResource) {
    const container = this.ensureBadgeContainer();
    if (!container) return;

    let contentEl = container.querySelector('[data-badge-content]');
    let discountEl = container.querySelector('[data-badge-discount]');

    // If the merchant moved markup around, keep it resilient.
    if (!contentEl) {
      contentEl = document.createElement('div');
      contentEl.className = 'product-information__variant-badge-content';
      contentEl.setAttribute('data-badge-content', '');
      container.appendChild(contentEl);
    }
    if (!discountEl) {
      discountEl = document.createElement('div');
      discountEl.className = 'product-information__variant-badge-discount';
      discountEl.setAttribute('data-badge-discount', '');
      container.appendChild(discountEl);
    }

    const content = typeof badge.content === 'string' ? badge.content.trim() : '';
    if (!content) {
      this.clearBadge();
      return;
    }

    if (contentEl) {
      // Metaobject content might include markup; keep it.
      contentEl.innerHTML = content;
      if (badge.contentFontSize) {
        contentEl.style.setProperty('--product-information-badge-content-font-size', `${badge.contentFontSize}px`);
      }
      if (badge.contentColor) contentEl.style.setProperty('--product-information-badge-content-color', badge.contentColor);
      if (badge.contentBg) contentEl.style.setProperty('--product-information-badge-content-bg', badge.contentBg);
    }

    if (discountEl) {
      discountEl.classList.toggle(
        'product-information__variant-badge-discount--desktop-only',
        !badge.discountVisibleMobile
      );

      if (!badge.discountVisible) {
        discountEl.textContent = '';
        discountEl.classList.remove('show-discount');
      } else if (!variantResource) {
        // On first load we might not have the variant payload yet; keep server-rendered state.
      } else {
        const discountPercentage = computeDiscountPercentage(variantResource);
        const showDiscount = discountPercentage > 0;

        if (badge.discountFontSize) {
          discountEl.style.setProperty('--product-information-badge-discount-font-size', `${badge.discountFontSize}px`);
        }
        if (badge.discountColor) discountEl.style.setProperty('--product-information-badge-discount-color', badge.discountColor);
        if (badge.discountBg) discountEl.style.setProperty('--product-information-badge-discount-bg', badge.discountBg);

        if (showDiscount) {
          const lang =
            (this.badgeContainer && this.badgeContainer.dataset.lang) ||
            document.documentElement.lang || '';
          const isEnglish = lang.toLowerCase().startsWith('en');
          let text;
          if (isEnglish) {
            // English: no leading minus and include Off suffix
            text = `${discountPercentage}% Off`;
          } else {
            // Other languages: leading minus, no suffix
            text = `-${discountPercentage}%`;
          }
          discountEl.textContent = text;
          discountEl.classList.add('show-discount');
        } else {
          discountEl.textContent = '';
          discountEl.classList.remove('show-discount');
        }
      }
    }

    container.classList.remove('hidden');
  }

  clearBadge() {
    const container = this.ensureBadgeContainer();
    if (!container) return;
    container.classList.add('hidden');

    const contentEl = container.querySelector('[data-badge-content]');
    if (contentEl) contentEl.innerHTML = '';

    const discountEl = container.querySelector('[data-badge-discount]');
    if (discountEl) {
      discountEl.textContent = '';
      discountEl.classList.remove('show-discount');
    }
  }

  static initAll() {
    document.querySelectorAll('[data-product-badge-container]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      if (container.dataset.productBadgeUpdaterInitialized === 'true') return;
      container.dataset.productBadgeUpdaterInitialized = 'true';
      new ProductBadgeUpdater(container);
    });
  }
}

if (!window.__productBadgeUpdaterBootstrapped) {
  window.__productBadgeUpdaterBootstrapped = true;

  document.addEventListener('DOMContentLoaded', () => {
    ProductBadgeUpdater.initAll();
  });
  document.addEventListener('shopify:section:load', () => {
    ProductBadgeUpdater.initAll();
  });
}

window.ProductBadgeUpdater = ProductBadgeUpdater;
